import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Clock,
  Plane,
  MapPin,
  Moon,
  Navigation,
  TrendingUp,
  Award,
  Calendar,
  Activity,
  Target,
  Zap,
  Filter,
  ChevronDown,
  Layers,
  Wind,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

const getAuthHeaders = (navigate) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) {
    toast.error('Authentication required. Please login again.');
    navigate('/login');
    return null;
  }
  return { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

const handleAuthError = (error, navigate) => {
  if (error.response?.status === 401) {
    toast.error('Session expired. Please login again.');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/login');
    return true;
  }
  return false;
};

const DATE_RANGES = [
  { label: 'Last 30 Days', value: '30d' },
  { label: 'Last 90 Days', value: '90d' },
  { label: 'Last 6 Months', value: '6m' },
  { label: 'Last 12 Months', value: '12m' },
  { label: 'Year to Date', value: 'ytd' },
  { label: 'Last Year', value: 'lastyear' },
  { label: 'All Time', value: 'all' },
  { label: 'Custom', value: 'custom' },
];

const COLORS = {
  primary:   '#2563eb',
  secondary: '#06b6d4',
  tertiary:  '#8b5cf6',
  success:   '#10b981',
  warning:   '#f59e0b',
  danger:    '#ef4444',
  mel:       '#f97316',
  turbine:   '#ec4899',
  gray:      '#6b7280'
};

const PIE_COLORS = [
  COLORS.primary, COLORS.success, COLORS.tertiary,
  COLORS.warning, COLORS.mel, COLORS.turbine, COLORS.danger
];

const Statistics = () => {
  const [flights, setFlights]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab]     = useState('overview');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const authHeaders = getAuthHeaders(navigate);
      if (!authHeaders) return;
      const flightsRes = await axios.get('/api/flights/all', authHeaders);
      setFlights(flightsRes.data.flights || flightsRes.data || []);
    } catch (err) {
      if (!handleAuthError(err, navigate)) {
        setError('Failed to load statistics data');
        toast.error('Failed to load statistics');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatTime = (h) => {
    if (!h || h === 0 || h === null || h === undefined) return '0.0';
    const n = typeof h === 'string' ? parseFloat(h) : h;
    return isNaN(n) ? '0.0' : n.toFixed(1);
  };

  const parseDate = (d) => {
    if (!d) return new Date();
    if (d instanceof Date) return d;
    if (typeof d === 'string') {
      if (d.match(/^\d{4}-\d{2}-\d{2}$/)) return new Date(d + 'T00:00:00');
      const p = new Date(d);
      if (!isNaN(p.getTime())) return p;
    }
    if (typeof d === 'number') {
      if (d > 946684800 && d < 4102444800) return new Date(d * 1000);
      if (d > 946684800000) return new Date(d);
      if (d > 25000 && d < 100000) return new Date((d - 25569) * 86400 * 1000);
    }
    const f = new Date(d);
    return isNaN(f.getTime()) ? new Date() : f;
  };

  // Filter flights by selected date range
  const filteredFlights = useMemo(() => {
    if (dateRange === 'all') return flights;
    const now = new Date();
    let start, end;
    end = now;
    if (dateRange === '30d')      start = new Date(now - 30 * 86400000);
    else if (dateRange === '90d') start = new Date(now - 90 * 86400000);
    else if (dateRange === '6m')  start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    else if (dateRange === '12m') start = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
    else if (dateRange === 'ytd') start = new Date(now.getFullYear(), 0, 1);
    else if (dateRange === 'lastyear') {
      start = new Date(now.getFullYear() - 1, 0, 1);
      end   = new Date(now.getFullYear() - 1, 11, 31);
    } else if (dateRange === 'custom') {
      start = customStart ? new Date(customStart + 'T00:00:00') : null;
      end   = customEnd   ? new Date(customEnd   + 'T23:59:59') : null;
    }
    return flights.filter(f => {
      const d = parseDate(f.date);
      if (start && d < start) return false;
      if (end   && d > end)   return false;
      return true;
    });
  }, [flights, dateRange, customStart, customEnd]);

  // Aggregate totals from filtered flights
  const filteredTotals = useMemo(() => {
    return filteredFlights.reduce((acc, f) => {
      acc.totalFlights    += 1;
      acc.totalTime       += parseFloat(f.total_time)            || 0;
      acc.picTime         += parseFloat(f.pic_time)              || 0;
      acc.sicTime         += parseFloat(f.sic_time)              || 0;
      acc.nightTime       += parseFloat(f.night_time)            || 0;
      acc.instrumentTime  += parseFloat(f.actual_instrument)     || 0;
      acc.simInstrument   += parseFloat(f.simulated_instrument)  || 0;
      acc.melTime         += parseFloat(f.amel_time)             || 0;
      acc.turbineTime     += parseFloat(f.turbine_time)          || 0;
      acc.turboFanTime    += parseFloat(f.turbo_fan_time)        || 0;
      acc.crossCountry    += parseFloat(f.cross_country_time)    || 0;
      acc.dualReceived    += parseFloat(f.dual_received)         || 0;
      acc.dualGiven       += parseFloat(f.dual_given)            || 0;
      acc.dayLandings     += parseInt(f.day_landings)            || 0;
      acc.nightLandings   += parseInt(f.night_landings)          || 0;
      acc.dayTakeoffs     += parseInt(f.day_takeoffs)            || 0;
      acc.nightTakeoffs   += parseInt(f.night_takeoffs)          || 0;
      return acc;
    }, {
      totalFlights: 0, totalTime: 0, picTime: 0, sicTime: 0,
      nightTime: 0, instrumentTime: 0, simInstrument: 0,
      melTime: 0, turbineTime: 0, turboFanTime: 0, crossCountry: 0,
      dualReceived: 0, dualGiven: 0, dayLandings: 0, nightLandings: 0,
      dayTakeoffs: 0, nightTakeoffs: 0
    });
  }, [filteredFlights]);

  const getMonthlyData = () => {
    const map = {};
    filteredFlights.forEach(f => {
      if (!f.date) return;
      const d = parseDate(f.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map[key]) map[key] = { month: key, flights: 0, totalTime: 0, picTime: 0, nightTime: 0, melTime: 0, instrumentTime: 0 };
      map[key].flights++;
      map[key].totalTime       += parseFloat(f.total_time)        || 0;
      map[key].picTime         += parseFloat(f.pic_time)          || 0;
      map[key].nightTime       += parseFloat(f.night_time)        || 0;
      map[key].melTime         += parseFloat(f.amel_time)         || 0;
      map[key].instrumentTime  += parseFloat(f.actual_instrument) || 0;
    });
    return Object.values(map)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-24)
      .map(item => ({
        ...item,
        totalTime:      parseFloat(item.totalTime.toFixed(1)),
        picTime:        parseFloat(item.picTime.toFixed(1)),
        nightTime:      parseFloat(item.nightTime.toFixed(1)),
        melTime:        parseFloat(item.melTime.toFixed(1)),
        instrumentTime: parseFloat(item.instrumentTime.toFixed(1)),
        monthName: new Date(item.month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
      }));
  };

  const getYearlyData = () => {
    const map = {};
    flights.forEach(f => {
      if (!f.date) return;
      const year = String(parseDate(f.date).getFullYear());
      if (!map[year]) map[year] = { year, flights: 0, totalTime: 0, picTime: 0, nightTime: 0, melTime: 0, instrumentTime: 0 };
      map[year].flights++;
      map[year].totalTime       += parseFloat(f.total_time)        || 0;
      map[year].picTime         += parseFloat(f.pic_time)          || 0;
      map[year].nightTime       += parseFloat(f.night_time)        || 0;
      map[year].melTime         += parseFloat(f.amel_time)         || 0;
      map[year].instrumentTime  += parseFloat(f.actual_instrument) || 0;
    });
    return Object.values(map)
      .sort((a, b) => a.year.localeCompare(b.year))
      .map(y => ({
        ...y,
        totalTime:      parseFloat(y.totalTime.toFixed(1)),
        picTime:        parseFloat(y.picTime.toFixed(1)),
        nightTime:      parseFloat(y.nightTime.toFixed(1)),
        melTime:        parseFloat(y.melTime.toFixed(1)),
        instrumentTime: parseFloat(y.instrumentTime.toFixed(1)),
      }));
  };

const getAircraftTypeData = () => {
  const map = {};

  const normalizeAircraftType = (type) => {
    if (!type) return 'Unknown';

    // Boeing 737 family — all variants
    if (['B737', 'B738', 'B739', 'B37M', 'B38M', 'B39M'].includes(type)) return 'Boeing 737';

    // Airbus family
    if (['A319', 'A320', 'A321'].includes(type)) return 'Airbus';

    // Regional
    if (['DHC8', 'DH4'].includes(type)) return 'Bombardier Q400';
    if (['E175', 'E75', 'E7W'].includes(type)) return 'Embraer 175';
    if (['CRJ200', 'CRJ700', 'CRJ'].includes(type)) return 'CRJ';

    return type; // Leave anything else as-is
  };

  filteredFlights.forEach(f => {
    const type = normalizeAircraftType(f.aircraft_type);
    if (!map[type]) map[type] = { type, flights: 0, totalTime: 0, melTime: 0 };
    map[type].flights++;
    map[type].totalTime += parseFloat(f.total_time) || 0;
    map[type].melTime   += parseFloat(f.amel_time)  || 0;
  });

  return Object.values(map)
    .sort((a, b) => b.totalTime - a.totalTime)
    .slice(0, 8)
    .map(t => ({
      ...t,
      totalTime: parseFloat(t.totalTime.toFixed(1)),
      melTime:   parseFloat(t.melTime.toFixed(1))
    }));
};
  const getTimeBreakdownData = () => {
    const t = filteredTotals;
    return [
      { name: 'PIC',          value: parseFloat(t.picTime.toFixed(1)) },
      { name: 'SIC',          value: parseFloat(t.sicTime.toFixed(1)) },
      { name: 'Night',        value: parseFloat(t.nightTime.toFixed(1)) },
      { name: 'MEL',          value: parseFloat(t.melTime.toFixed(1)) },
      { name: 'Actual IMC',   value: parseFloat(t.instrumentTime.toFixed(1)) },
      { name: 'Cross Country',value: parseFloat(t.crossCountry.toFixed(1)) },
      { name: 'Turbine',      value: parseFloat(t.turbineTime.toFixed(1)) },
    ].filter(i => i.value > 0);
  };

  const getTopAirports = () => {
    const map = {};
    filteredFlights.forEach(f => {
      [f.departure_airport, f.arrival_airport].forEach(a => {
        if (a) map[a] = (map[a] || 0) + 1;
      });
    });
    return Object.entries(map).map(([airport, visits]) => ({ airport, visits }))
      .sort((a, b) => b.visits - a.visits).slice(0, 10);
  };

  const getDurationData = () => {
    const ranges = { '0-1 hrs': 0, '1-2 hrs': 0, '2-3 hrs': 0, '3-4 hrs': 0, '4-5 hrs': 0, '5+ hrs': 0 };
    filteredFlights.forEach(f => {
      const d = parseFloat(f.total_time) || 0;
      if (d <= 1) ranges['0-1 hrs']++;
      else if (d <= 2) ranges['1-2 hrs']++;
      else if (d <= 3) ranges['2-3 hrs']++;
      else if (d <= 4) ranges['3-4 hrs']++;
      else if (d <= 5) ranges['4-5 hrs']++;
      else ranges['5+ hrs']++;
    });
    return Object.entries(ranges).map(([range, count]) => ({ range, count }));
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg text-sm">
        <p className="font-semibold text-gray-800 mb-1">{label}</p>
        {payload.map((e, i) => (
          <p key={i} style={{ color: e.color }}>
            {e.name}: {typeof e.value === 'number' ? e.value.toFixed(1) : e.value}
          </p>
        ))}
      </div>
    );
  };

  // Blue and gray only; `highlight` picks out a tile with a light-blue background
  const StatCard = ({ title, value, icon: Icon, subtitle = '', highlight = false }) => (
    <div className={`rounded-xl p-5 transition-shadow duration-200 hover:shadow-md border ${highlight ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-100 shadow-sm'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{title}</p>
          <p className={`text-2xl font-bold truncate ${highlight ? 'text-blue-700' : 'text-gray-900'}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`ml-3 p-2.5 rounded-lg flex-shrink-0 ${highlight ? 'bg-blue-100' : 'bg-blue-50'}`}>
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
      </div>
    </div>
  );

  const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-center mb-5">
      <div className="p-2 bg-aviation-100 rounded-lg mr-3">
        <Icon className="h-5 w-5 text-aviation-600" />
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviation-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading your flight statistics...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="text-red-600 text-xl mb-4">{error}</div>
        <button onClick={fetchData} className="px-4 py-2 bg-aviation-600 text-white rounded-lg hover:bg-aviation-700 transition-colors">Retry</button>
      </div>
    </div>
  );

  const monthlyData     = getMonthlyData();
  const yearlyData      = getYearlyData();
  const aircraftData    = getAircraftTypeData();
  const timeBreakdown   = getTimeBreakdownData();
  const topAirports     = getTopAirports();
  const durationData    = getDurationData();
  const t               = filteredTotals;
  const rangeLabel      = DATE_RANGES.find(r => r.value === dateRange)?.label || 'All Time';
  const isFiltered      = dateRange !== 'all';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <BarChart3 className="h-8 w-8 mr-3 text-aviation-600" />
            Flight Statistics
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            {isFiltered
              ? `Showing ${filteredFlights.length} of ${flights.length} flights — ${rangeLabel}`
              : `All ${flights.length} flights`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${showFilters ? 'bg-aviation-600 text-white border-aviation-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
          >
            <Filter className="h-4 w-4" />
            Filters
            <ChevronDown className={`h-3 w-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Filters Panel ── */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
          <p className="text-sm font-semibold text-gray-700 mb-3">Date Range</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {DATE_RANGES.map(r => (
              <button
                key={r.value}
                onClick={() => setDateRange(r.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                  dateRange === r.value
                    ? 'bg-aviation-600 text-white border-aviation-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {dateRange === 'custom' && (
            <div className="flex gap-4 flex-wrap">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-aviation-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Date</label>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-aviation-500" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab Nav ── */}
      <div className="flex border-b border-gray-200 mb-8 gap-1 overflow-x-auto">
        {[
          { id: 'overview',  label: 'Overview',    icon: BarChart3 },
          { id: 'time',      label: 'Time Details', icon: Clock },
          { id: 'trends',    label: 'Trends',       icon: TrendingUp },
          { id: 'airports',  label: 'Airports',     icon: MapPin },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-aviation-600 text-aviation-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════
          TAB: OVERVIEW
      ══════════════════════════════════════════ */}
       {/* ({allTimeTotal > 0 ? ((allTimeMEL / allTimeTotal) * 100).toFixed(1) : 0}% of total flight time) */}
      {activeTab === 'overview' && (
        <>
          {/* Primary stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
            <StatCard title="Total Flights"    value={t.totalFlights}                    icon={Plane} />
            <StatCard title="Total Time"       value={`${formatTime(t.totalTime)} hrs`}  icon={Clock} />
            <StatCard title="PIC Time"         value={`${formatTime(t.picTime)} hrs`}    icon={Navigation} />
            <StatCard title="SIC Time"         value={`${formatTime(t.sicTime)} hrs`}    icon={Navigation} />
            <StatCard title="Multiengine (MEL)" value={`${formatTime(t.melTime)} hrs`}   icon={Layers}      highlight={true} subtitle={`${(t.totalTime > 0 ? ((t.melTime / t.totalTime) * 100).toFixed(1) : 0)} % of total time`} />
            <StatCard title="Night Time"       value={`${formatTime(t.nightTime)} hrs`}  icon={Moon} />
            <StatCard title="Actual IMC"       value={`${formatTime(t.instrumentTime)} hrs`} icon={Activity} />
            <StatCard title="Turbine Time"     value={`${formatTime(t.turbineTime)} hrs`} icon={Zap} />
            <StatCard title="Cross Country"    value={`${formatTime(t.crossCountry)} hrs`} icon={Wind} />
            <StatCard title="Total Landings"   value={t.dayLandings + t.nightLandings}  icon={Target}
              subtitle={`${t.dayLandings} day · ${t.nightLandings} night`} />
            <StatCard title="Total Takeoffs"   value={t.dayTakeoffs + t.nightTakeoffs}  icon={TrendingUp}
              subtitle={`${t.dayTakeoffs} day · ${t.nightTakeoffs} night`} />
            <StatCard title="Dual Given"       value={`${formatTime(t.dualGiven)} hrs`} icon={Award} />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6">
              <SectionHeader icon={Plane} title="Hours by Aircraft Type" />
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aircraftData} margin={{ bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="type" angle={-35} textAnchor="end" height={60} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="totalTime" fill={COLORS.primary}   name="Total Hours" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6">
              <SectionHeader icon={Clock} title="Time Breakdown" subtitle="Distribution of logged hours by category" />
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={timeBreakdown} cx="50%" cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => percent > 0.04 ? `${name}: ${value.toFixed(0)}h` : ''}
                      outerRadius={90} dataKey="value"
                    >
                      {timeBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v.toFixed(1)} hrs`]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Flight duration */}
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 mb-6">
            <SectionHeader icon={Zap} title="Flight Duration Distribution" />
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={durationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill={COLORS.tertiary} name="Flights" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════
          TAB: TIME DETAILS
      ══════════════════════════════════════════ */}
      {activeTab === 'time' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Detailed time table */}
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6">
              <SectionHeader icon={Clock} title="All Time Categories" subtitle={rangeLabel} />
              <div className="space-y-3">
                {[
                  { label: 'Total Flight Time',      value: t.totalTime,       pct: 100 },
                  { label: 'PIC Time',               value: t.picTime,         pct: t.totalTime ? (t.picTime / t.totalTime) * 100 : 0 },
                  { label: 'SIC Time',               value: t.sicTime,         pct: t.totalTime ? (t.sicTime / t.totalTime) * 100 : 0 },
                  { label: 'Multiengine Land (MEL)', value: t.melTime,         pct: t.totalTime ? (t.melTime / t.totalTime) * 100 : 0 },
                  { label: 'Turbine Time',           value: t.turbineTime,     pct: t.totalTime ? (t.turbineTime / t.totalTime) * 100 : 0 },
                  { label: 'Turbo-Fan Time',         value: t.turboFanTime,    pct: t.totalTime ? (t.turboFanTime / t.totalTime) * 100 : 0 },
                  { label: 'Night Time',             value: t.nightTime,       pct: t.totalTime ? (t.nightTime / t.totalTime) * 100 : 0 },
                  { label: 'Actual Instrument',      value: t.instrumentTime,  pct: t.totalTime ? (t.instrumentTime / t.totalTime) * 100 : 0 },
                  { label: 'Simulated Instrument',   value: t.simInstrument,   pct: t.totalTime ? (t.simInstrument / t.totalTime) * 100 : 0 },
                  { label: 'Cross Country',          value: t.crossCountry,    pct: t.totalTime ? (t.crossCountry / t.totalTime) * 100 : 0 },
                  { label: 'Dual Received',          value: t.dualReceived,    pct: t.totalTime ? (t.dualReceived / t.totalTime) * 100 : 0 },
                  { label: 'Dual Given',             value: t.dualGiven,       pct: t.totalTime ? (t.dualGiven / t.totalTime) * 100 : 0 },
                ].map(row => (
                  <div key={row.label}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-gray-700">{row.label}</span>
                      <span className="text-sm font-bold text-gray-900">{formatTime(row.value)} hrs</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(row.pct, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Landings & takeoffs + yearly summary */}
            <div className="space-y-6">
              <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6">
                <SectionHeader icon={Target} title="Landings & Takeoffs" />
                <div className="grid grid-cols-2 gap-4">
                  {[
                    // Day in blue, night in gray
                    { label: 'Day Landings',    value: t.dayLandings,   color: 'text-blue-700', bg: 'bg-blue-50' },
                    { label: 'Night Landings',  value: t.nightLandings, color: 'text-gray-800', bg: 'bg-gray-100' },
                    { label: 'Day Takeoffs',    value: t.dayTakeoffs,   color: 'text-blue-700', bg: 'bg-blue-50' },
                    { label: 'Night Takeoffs',  value: t.nightTakeoffs, color: 'text-gray-800', bg: 'bg-gray-100' },
                  ].map(r => (
                    <div key={r.label} className={`${r.bg} rounded-xl p-4 text-center`}>
                      <div className={`text-3xl font-bold ${r.color}`}>{r.value}</div>
                      <div className="text-xs text-gray-500 mt-1">{r.label}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-gray-800">{t.dayLandings + t.nightLandings}</div>
                    <div className="text-xs text-gray-500">Total Landings</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-800">{t.dayTakeoffs + t.nightTakeoffs}</div>
                    <div className="text-xs text-gray-500">Total Takeoffs</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Yearly breakdown table */}
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6">
            <SectionHeader icon={Calendar} title="Year-by-Year Breakdown" subtitle="All time, ungrouped" />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    {['Year','Flights','Total','PIC','Night','MEL','Actual IMC'].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {yearlyData.slice().reverse().map((row, i) => (
                    <tr key={row.year} className={`border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="py-2.5 px-3 font-semibold text-gray-800">{row.year}</td>
                      <td className="py-2.5 px-3 text-gray-700">{row.flights}</td>
                      <td className="py-2.5 px-3 text-gray-700">{row.totalTime.toFixed(1)}</td>
                      <td className="py-2.5 px-3 text-green-700 font-medium">{row.picTime.toFixed(1)}</td>
                      <td className="py-2.5 px-3 text-purple-700">{row.nightTime.toFixed(1)}</td>
                      <td className="py-2.5 px-3 text-orange-600 font-semibold">{row.melTime.toFixed(1)}</td>
                      <td className="py-2.5 px-3 text-yellow-700">{row.instrumentTime.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td className="py-2.5 px-3 text-gray-800">Total</td>
                    <td className="py-2.5 px-3">{yearlyData.reduce((s,r) => s + r.flights, 0)}</td>
                    <td className="py-2.5 px-3">{yearlyData.reduce((s,r) => s + r.totalTime, 0).toFixed(1)}</td>
                    <td className="py-2.5 px-3 text-green-700">{yearlyData.reduce((s,r) => s + r.picTime, 0).toFixed(1)}</td>
                    <td className="py-2.5 px-3 text-purple-700">{yearlyData.reduce((s,r) => s + r.nightTime, 0).toFixed(1)}</td>
                    <td className="py-2.5 px-3 text-orange-600">{yearlyData.reduce((s,r) => s + r.melTime, 0).toFixed(1)}</td>
                    <td className="py-2.5 px-3 text-yellow-700">{yearlyData.reduce((s,r) => s + r.instrumentTime, 0).toFixed(1)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════
          TAB: TRENDS
      ══════════════════════════════════════════ */}
      {activeTab === 'trends' && (
        <>
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 mb-6">
            <SectionHeader icon={TrendingUp} title="Monthly Flight Activity" subtitle="Last 24 months" />
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="monthName" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="totalTime"  stroke={COLORS.primary}   fill={COLORS.primary}   fillOpacity={0.15} name="Total Hours" strokeWidth={2} />
                  <Area type="monotone" dataKey="picTime"    stroke={COLORS.success}   fill={COLORS.success}   fillOpacity={0.1}  name="PIC Hours"   strokeWidth={2} />
                  <Area type="monotone" dataKey="melTime"    stroke={COLORS.mel}       fill={COLORS.mel}       fillOpacity={0.15} name="MEL Hours"   strokeWidth={2} />
                  <Area type="monotone" dataKey="nightTime"  stroke={COLORS.tertiary}  fill={COLORS.tertiary}  fillOpacity={0.1}  name="Night Hours"  strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 mb-6">
            <SectionHeader icon={Calendar} title="Annual Hours by Category" subtitle="All years" />
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={yearlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="totalTime"      fill={COLORS.primary}   name="Total Hours"  radius={[2,2,0,0]} />
                  <Bar dataKey="melTime"        fill={COLORS.mel}       name="MEL Hours"    radius={[2,2,0,0]} />
                  <Bar dataKey="nightTime"      fill={COLORS.tertiary}  name="Night Hours"  radius={[2,2,0,0]} />
                  <Bar dataKey="instrumentTime" fill={COLORS.warning}   name="Actual IMC"   radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6">
            <SectionHeader icon={Activity} title="Monthly Flight Count" />
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="monthName" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="flights" stroke={COLORS.secondary} strokeWidth={2.5} dot={false} name="Flights" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════
          TAB: AIRPORTS
      ══════════════════════════════════════════ */}
      {activeTab === 'airports' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6">
              <SectionHeader icon={MapPin} title="Most Visited Airports" subtitle={`${rangeLabel} · ${topAirports.length} airports`} />
              <div className="space-y-2">
                {topAirports.map((airport, i) => (
                  <div key={airport.airport} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      i === 0 ? 'bg-yellow-100 text-yellow-700' :
                      i === 1 ? 'bg-gray-200 text-gray-600' :
                      i === 2 ? 'bg-orange-100 text-orange-600' :
                      'bg-blue-50 text-blue-600'
                    }`}>{i + 1}</div>
                    <span className="font-semibold text-gray-900 flex-1">{airport.airport}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 rounded-full bg-aviation-200 w-24 hidden sm:block">
                        <div className="h-1.5 rounded-full bg-aviation-500"
                          style={{ width: `${(airport.visits / topAirports[0].visits) * 100}%` }} />
                      </div>
                      <span className="text-sm font-bold text-gray-600 w-16 text-right">{airport.visits} visits</span>
                    </div>
                  </div>
                ))}
                {topAirports.length === 0 && <p className="text-gray-400 text-center py-6">No airport data available</p>}
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6">
              <SectionHeader icon={BarChart3} title="Top Airports by Visit" />
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={topAirports.slice(0, 10)} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="airport" tick={{ fontSize: 12 }} width={45} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="visits" fill={COLORS.primary} name="Visits" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default Statistics;